import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  assignmentActivityByUserReportType,
  assignmentActivityReportByPortalsType,
  AssignmentRecord,
  summaryType,
} from "../../../../../services/api/types/reports/reports";
import { AssignmentStatus } from "../../../../../services/api/types/userAssignment/types";
import { RecordStatus } from "../../../../../services/api/types/editTraniningRecords/editTrainingRecords";
import { formatKey, formatFilterRow } from "../../utils/helpers";

const getAssignmentStatusLabel = (record: AssignmentRecord): string =>
  record.status === AssignmentStatus.ACTIVE &&
  record.recordStatus === RecordStatus.ACTIVE
    ? "Active"
    : "Inactive";

const toDateOnly = (value: string | null | undefined): string =>
  (value ?? "").split(",")[0];

interface ExportLearningPathExcelParams {
  reportType: string;
  generatedBy: string;
  time: string;
  filters: Record<string, any>;
  detailedData:
    | assignmentActivityByUserReportType[]
    | assignmentActivityReportByPortalsType[];
  summaryData: summaryType[];
  isGroupedByPortal: boolean;
  fileName: string;
}

export const exportToExcel = ({
  reportType,
  generatedBy,
  time,
  filters,
  detailedData,
  summaryData,
  isGroupedByPortal = false,
  fileName,
}: ExportLearningPathExcelParams) => {
  // Flatten the data structure if grouped by portal
  let flattenedData: assignmentActivityByUserReportType[] = [];

  if (isGroupedByPortal) {
    const portalData = (detailedData ??
      []) as assignmentActivityReportByPortalsType[];
    portalData.forEach((portal) => {
      (portal.users ?? []).forEach((user) => {
        flattenedData.push({
          userUid: user.userUid ?? "",
          portalUid: portal.portalUid ?? "",
          portalName: portal.name ?? "",
          parentPortalUid: portal.parentPortalUid ?? "",
          firstName: user.firstName ?? "",
          lastName: user.lastName ?? "",
          email: user.email ?? "",
          userAttributes: user.userAttributes ?? {},
          assignments: user.assignments ?? [],
        });
      });
    });
  } else {
    flattenedData = (detailedData ??
      []) as assignmentActivityByUserReportType[];
  }

  // Collect all unique attribute keys from all users
  const allAttributeKeys = new Set<string>();
  flattenedData.forEach((user) => {
    if (user.userAttributes) {
      Object.keys(user.userAttributes).forEach((key) =>
        allAttributeKeys.add(key),
      );
    }
  });

  const hasUserAttributes = allAttributeKeys.size > 0;

  // Sheet 1: Definition
  const definitionSheetData: any[] = [
    ["Report type:", reportType || ""],
    ["Generated by:", generatedBy || ""],
    ["Time:", time || ""],
    ["Applied Filters"],
  ];

  const generalFilterRow = formatFilterRow([
    ["Portal", filters.general?.Portal],
    ["Include subportals", filters.general?.GroupByPortal],
  ]);
  if (generalFilterRow.length > 0) {
    definitionSheetData.push(generalFilterRow);
  }

  // Only add User Attributes section if there are user attributes with values
  if (
    hasUserAttributes &&
    filters.userAttributes &&
    Object.keys(filters.userAttributes).length > 0
  ) {
    const userAttributesRow = formatFilterRow(
      Object.entries(filters.userAttributes).map(
        ([key, value]) => [formatKey(key), value] as [string, unknown],
      ),
    );
    if (userAttributesRow.length > 0) {
      definitionSheetData.push(["User Attributes"]);
      definitionSheetData.push(userAttributesRow);
    }
  }

  const assignmentRow1 = formatFilterRow([
    ["Assignment", filters.assignments?.Assignment],
    ["Assignment user status", filters.assignments?.AssignmentStatus],
    ["Progress status", filters.assignments?.ProgressStatus],
  ]);
  const assignmentRow2 = formatFilterRow([
    ["Deadline", filters.assignments?.DueDate],
    ["Start date", filters.assignments?.StartDate],
    ["Completion", filters.assignments?.EndDate],
    ["Assign date", filters.assignments?.CreatedAt],
  ]);
  if (assignmentRow1.length > 0 || assignmentRow2.length > 0) {
    definitionSheetData.push(["Assignments"]);
    if (assignmentRow1.length > 0) definitionSheetData.push(assignmentRow1);
    if (assignmentRow2.length > 0) definitionSheetData.push(assignmentRow2);
  }

  const definitionSheet = XLSX.utils.aoa_to_sheet(definitionSheetData);

  // Sheet 2: Data (Details Table) - One row per assignment
  const detailedSheetData: Record<string, any>[] = [];

  flattenedData.forEach((user) => {
    // If user has no assignments, create one row with user info only
    if (!user.assignments || user.assignments.length === 0) {
      const row: Record<string, any> = {
        PortalUid: user.portalUid || "",
        ParentPortalUid: user.parentPortalUid || "",
        "First name": user.firstName || "",
        "Last name": user.lastName || "",
        Email: user.email || "",
      };

      // Add all attributes only if there are any attributes
      if (hasUserAttributes) {
        allAttributeKeys.forEach((key) => {
          const value = user.userAttributes?.[key];
          row[key] =
            value === null || value === undefined || value === "" ? "" : value;
        });
      }

      // Add assignment fields with "/" since there are no assignments
      row["Record No."] = "";
      row["Assignment title"] = "";
      row["Placement"] = "";
      row["Code"] = "";
      row["SKU"] = "";
      row["Version"] = "";
      row["Category"] = "";
      row["Deadline"] = "";
      row["Assign Date"] = "";
      row["Status"] = "";
      row["Progress status"] = "";
      row["Time spent"] = "";
      row["Start Date"] = "";
      row["Start Time (UTC)"] = "";
      row["Completion Date"] = "";
      row["Completion Time (UTC)"] = "";
      row["Best Score"] = "";
      row["Scores"] = "";
      row["No. of Attempts"] = "";
      row["Certificate ID"] = "";

      detailedSheetData.push(row);
    } else {
      // Create one row for each record of each assignment
      user.assignments.forEach((assignment) => {
        const sortedRecords = [...(assignment.records ?? [])].sort(
          (a, b) => a.recordNo - b.recordNo,
        );

        sortedRecords.forEach((record: AssignmentRecord) => {
          const row: Record<string, any> = {};

          row.PortalUid = user.portalUid || "";
          row.ParentPortalUid = user.parentPortalUid || "";
          row["First name"] = user.firstName || "";
          row["Last name"] = user.lastName || "";
          row.Email = user.email || "";

          // Add all attributes only if there are any attributes and only in the first row
          if (hasUserAttributes) {
            allAttributeKeys.forEach((key) => {
              const value = user.userAttributes?.[key];
              row[key] =
                value === null || value === undefined || value === ""
                  ? ""
                  : value;
            });
          }

          // Add assignment information for all rows
          row["Record No."] = record.recordNo ?? "";
          row["Assignment title"] = assignment.title || "";
          row["Placement"] = record.portalGroupName || "Homepage";
          row["Code"] = assignment.code || "";
          row["SKU"] = assignment.SKU || "";
          row["Version"] = record.version || "";
          row["Category"] = assignment.type || "";
          row["Deadline"] = record.dueDate ?? "";
          row["Assign Date"] = toDateOnly(record.assignDate);
          row["Status"] = getAssignmentStatusLabel(record);
          row["Progress status"] = record.progressStatus ?? "";
          row["Time spent"] = record.timeSpent ?? "";
          row["Start Date"] = record.startDate ?? "";
          row["Start Time (UTC)"] = record.startTime ?? "";
          row["Completion Date"] = record.completionDate ?? "";
          row["Completion Time (UTC)"] = record.completionTime ?? "";
          row["Best Score"] = record.bestScore ?? "";
          row["Scores"] = record.scores ?? "";
          row["No. of Attempts"] = record.attempts ?? "";
          row["Certificate ID"] = record.certificateUid ?? "";

          detailedSheetData.push(row);
        });
      });
    }
  });

  const detailedSheet = XLSX.utils.json_to_sheet(detailedSheetData);

  // Sheet 3: Activities (Summary Table)
  const summarySheetData = (summaryData ?? []).map((item) => ({
    "Assignment title/Reporting title": item.title,
    Code: item.code || "",
    SKU: item.SKU || "",
    Type: item.type || "",
    "Average Score": item.avgScore || "",
    "Average Completion Time": item.avgCompleteTime || "",
    "Minimum Completion Time": item.minCompleteTime || "",
    "Maximum Completion Time": item.maxCompleteTime || "",
    Assignments: item.assignedUsers || "",
    Completed: item.completedUsers || "",
    Pending: item.pendingUsers || "",
    Overdue: item.overdueUsers || "",
  }));

  const summarySheet = XLSX.utils.json_to_sheet(summarySheetData);

  // === Workbook ===
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, definitionSheet, "Info");
  XLSX.utils.book_append_sheet(workbook, detailedSheet, "Data");
  XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");

  // === Export ===
  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const blob = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  saveAs(blob, `${fileName}.xlsx`);
};
